/**
 * @file index.tsx
 *
 * @fileoverview Renders a h1 with the amount of results found on the search pages (jobs / companies / resumes).
 */

import React from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { Metatag } from '~/jobiqo/types/types';

/**
 * Returns appropriate search title.
 *
 * @param {number} amount
 *  The number of results found.
 * @param {string} type
 *  The type of results found.
 * @param {Metatag[]} metadata
 *  Metadata passed to this search.
 * @param {IntlShape} intl
 *  Intl shape.
 * @returns {string}
 *  Appropriate search title.
 */
export const getSearchLabel = ({
  amount,
  metadata,
  type,
  intl,
}: SearchTitleProps & { intl: IntlShape }): string => {
  // If there is a page tittle passed use that.
  const titleMetatag = metadata && metadata.filter((m) => m.meta === 'h1');
  if (titleMetatag && titleMetatag.length > 0) {
    return titleMetatag[0].value as string;
  }

  const amountType = amount + ' ' + type;

  if (amount === 1) {
    return (
      amountType +
      ' ' +
      intl.formatMessage({
        id: 'search_result_amount_singular',
        defaultMessage: ' found.',
      })
    );
  }
  return (
    amountType +
    intl.formatMessage({
      id: 'search_result_amount_plural',
      defaultMessage: 's found.',
    })
  );
};

export interface SearchTitleProps {
  /**
   * The number of results found.
   */
  amount: number;
  /**
   * The type of results found.
   */
  type: string;
  /**
   * Metadata passed to this search.
   */
  metadata: Metatag[];
}

/**
 * The result amount to use on search pages. Can be usd on job, company and resume searches.
 */
export const SearchTitle = (props: SearchTitleProps) => {
  const intl = useIntl();

  return <>{getSearchLabel({ ...props, intl })}</>;
};

export default SearchTitle;
